import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, TrendingUp, TrendingDown, Scale } from "lucide-react";
import { formatMoney } from "@finora/config";
import { EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { DateInputDDMMYYYY } from "../../components/DateInputDDMMYYYY";
import { DailyKasaReportTab } from "./DailyKasaReportTab";
import { MinimaxReportsTab } from "./MinimaxReportsTab";
import { PfrReceiptsExportTab } from "./PfrReceiptsExportTab";
import { BillingUsageReportTab } from "./BillingUsageReportTab";

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function currentMonthRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

function previousMonthRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

type Preset = "this-month" | "last-month" | "custom";

export function ReportsPage() {
  const [mainTab, setMainTab] = useState<"pdv" | "minimax" | "pfr-export" | "naplata" | "kasa">("pdv");
  const [preset, setPreset] = useState<Preset>("this-month");
  const [customFrom, setCustomFrom] = useState(toIsoDate(new Date()));
  const [customTo, setCustomTo] = useState(toIsoDate(new Date()));

  const range =
    preset === "this-month"
      ? currentMonthRange()
      : preset === "last-month"
        ? previousMonthRange()
        : { from: customFrom, to: customTo };

  const { data: report, isLoading } = useQuery({
    queryKey: ["reports-summary", range.from, range.to],
    queryFn: () => api.getReportSummary(range.from, range.to),
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Izveštaji</h1>
        <p className="text-sm text-slate-500">Ulazni/izlazni PDV, promet i nenaplaćene fakture</p>
      </div>

      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setMainTab("pdv")}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            mainTab === "pdv" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          PDV / Promet
        </button>
        <button
          onClick={() => setMainTab("naplata")}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            mainTab === "naplata" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Naplata usluga
        </button>
        <button
          onClick={() => setMainTab("minimax")}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            mainTab === "minimax" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          MiniMax
        </button>
        <button
          onClick={() => setMainTab("kasa")}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            mainTab === "kasa" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Dnevni izveštaj kasa
        </button>
        <button
          onClick={() => setMainTab("pfr-export")}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            mainTab === "pfr-export" ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Poreska uprava → MiniMax
        </button>
      </div>

      {mainTab === "kasa" ? (
        <DailyKasaReportTab />
      ) : mainTab === "minimax" ? (
        <MinimaxReportsTab />
      ) : mainTab === "naplata" ? (
        <BillingUsageReportTab />
      ) : mainTab === "pfr-export" ? (
        <PfrReceiptsExportTab />
      ) : (
        <>
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div className="flex gap-1">
          {(
            [
              { key: "this-month", label: "Ovaj mesec" },
              { key: "last-month", label: "Prošli mesec" },
              { key: "custom", label: "Od - do" },
            ] as { key: Preset; label: string }[]
          ).map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                preset === p.key ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-end gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Od</label>
              <DateInputDDMMYYYY value={customFrom} onChange={setCustomFrom} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Do</label>
              <DateInputDDMMYYYY value={customTo} onChange={setCustomTo} />
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : report ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
              <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                Izlazni PDV
              </div>
              <p className="text-2xl font-semibold text-slate-900">{formatMoney(report.outputVat)}</p>
              <p className="mt-1 text-xs text-slate-400">Promet: {formatMoney(report.turnover)}</p>
            </div>
            <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
              <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                <TrendingDown className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                Ulazni PDV
              </div>
              <p className="text-2xl font-semibold text-slate-900">{formatMoney(report.inputVat)}</p>
              <p className="mt-1 text-xs text-slate-400">Nabavka (neto): {formatMoney(report.purchaseNet)}</p>
            </div>
            <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
              <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                <Scale className="h-3.5 w-3.5 text-brand" aria-hidden />
                PDV za uplatu (razlika)
              </div>
              <p
                className={`text-2xl font-semibold ${
                  Number(report.vatBalance) < 0 ? "text-emerald-600" : "text-slate-900"
                }`}
              >
                {formatMoney(report.vatBalance)}
              </p>
              <p className="mt-1 text-xs text-slate-400">Izlazni - ulazni PDV</p>
            </div>
            <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
              <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                <AlertCircle className="h-3.5 w-3.5 text-red-500" aria-hidden />
                Nenaplaćeno (dospelo)
              </div>
              <p className="text-2xl font-semibold text-red-600">{formatMoney(report.overdueTotalAmount)}</p>
              <p className="mt-1 text-xs text-slate-400">{report.overdueInvoices.length} faktura kasni</p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-surface shadow-subtle">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-800">
                Dospele fakture koje kasne (trenutno stanje, bez obzira na period iznad)
              </h2>
            </div>
            {report.overdueInvoices.length === 0 ? (
              <div className="p-5">
                <EmptyState
                  icon={AlertCircle}
                  title="Nema nenaplaćenih dospelih faktura"
                  description="Sve fakture su naplaćene ili im rok plaćanja još nije istekao."
                />
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Broj</th>
                    <th>Kupac</th>
                    <th>Rok plaćanja</th>
                    <th>Kasni (dana)</th>
                    <th>Neplaćeno</th>
                  </tr>
                </thead>
                <tbody>
                  {report.overdueInvoices.map((inv) => (
                    <tr key={inv.id}>
                      <td className="font-medium text-slate-800">{inv.documentNumber ?? "—"}</td>
                      <td className="text-slate-600">{inv.customerName ?? "—"}</td>
                      <td className="text-slate-500">{new Date(inv.dueDate).toLocaleDateString("sr-RS")}</td>
                      <td className="text-red-600">{inv.daysOverdue}</td>
                      <td className="font-medium text-red-600">{formatMoney(inv.dueAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
        </>
      )}
    </div>
  );
}
